import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children"];
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { createEventChannel } from './core';
var emptyObject = {};
export var createBreadcrumbAPI = function createBreadcrumbAPI() {
  var context = /*#__PURE__*/createContext(function () {
    return undefined;
  });
  var useGetBreadcrumbs = function useGetBreadcrumbs() {
    return useContext(context);
  };
  var breadcrumbMountedEventChannel = createEventChannel();
  var useEventDispatch = breadcrumbMountedEventChannel.useEventDispatch;
  var EmitEventOnMount = function EmitEventOnMount(_ref) {
    var name = _ref.name,
      getMountedBreadcrumbPayload = _ref.getMountedBreadcrumbPayload;
    var eventDispatch = useEventDispatch();
    useEffect(function () {
      // Should always be defined at this point.
      eventDispatch(getMountedBreadcrumbPayload());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name]);
    return null;
  };
  var Breadcrumb = function Breadcrumb(_ref2) {
    var children = _ref2.children,
      breadcrumbPayload = _objectWithoutProperties(_ref2, _excluded);
    var getParentBreadcrumbs = useGetBreadcrumbs();

    // TODO: Type inference doesn't seem to understand that children and name are never a part of breadcrumbPayload. Investigate and fix.
    var castBreadcrumbPayload = breadcrumbPayload;

    // Prevents breadcrumb callback from being recreated everytime props change/every-render
    var breadcrumbRef = useRef(castBreadcrumbPayload);
    useLayoutEffect(function () {
      // Ensures that the breadcrumb only updates when it component is mounted
      breadcrumbRef.current = castBreadcrumbPayload;
    });

    // TODO: Memoize this in the future?
    var getMountedBreadcrumbPayload = useCallback(function () {
      var _getParentBreadcrumbs, _getParentBreadcrumbs2;
      return {
        breadcrumbs: [].concat(_toConsumableArray((_getParentBreadcrumbs = (_getParentBreadcrumbs2 = getParentBreadcrumbs()) === null || _getParentBreadcrumbs2 === void 0 ? void 0 : _getParentBreadcrumbs2.breadcrumbs) !== null && _getParentBreadcrumbs !== void 0 ? _getParentBreadcrumbs : []), [breadcrumbRef.current]),
        payload: breadcrumbRef.current
      };
    }, [getParentBreadcrumbs]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmitEventOnMount, {
      name: breadcrumbPayload.name,
      getMountedBreadcrumbPayload: getMountedBreadcrumbPayload
    }), /*#__PURE__*/React.createElement(context.Provider, {
      value: getMountedBreadcrumbPayload
    }, children));
  };
  return {
    /**
     * An event channel whos event represents that a breadcrumb was mounted
     */
    breadcrumbMountedEventChannel: breadcrumbMountedEventChannel,
    Breadcrumb: Breadcrumb,
    useGetBreadcrumbs: useGetBreadcrumbs
  };
};
export var createBreadcrumbedEventChannel = function createBreadcrumbedEventChannel(_ref3) {
  var useGetBreadcrumbs = _ref3.useGetBreadcrumbs;
  var createEventChannelFn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : createEventChannel;
  var _createEventChannelFn = createEventChannelFn(),
    Listener = _createEventChannelFn.Listener,
    useEventDispatch = _createEventChannelFn.useEventDispatch;
  var useBreadcrumbedEventDispatch = function useBreadcrumbedEventDispatch() {
    var eventDispatch = useEventDispatch();
    var getParentBreadcrumbs = useGetBreadcrumbs();
    var breadcrumbedEventDispatch = useCallback(function (payload) {
      var _getParentBreadcrumbs3, _getParentBreadcrumbs4;
      return eventDispatch({
        breadcrumbs: (_getParentBreadcrumbs3 = (_getParentBreadcrumbs4 = getParentBreadcrumbs()) === null || _getParentBreadcrumbs4 === void 0 ? void 0 : _getParentBreadcrumbs4.breadcrumbs) !== null && _getParentBreadcrumbs3 !== void 0 ? _getParentBreadcrumbs3 : [],
        payload: payload
      });
    }, [getParentBreadcrumbs, eventDispatch]);
    return breadcrumbedEventDispatch;
  };
  return {
    Listener: Listener,
    useEventDispatch: useEventDispatch,
    useBreadcrumbedEventDispatch: useBreadcrumbedEventDispatch
  };
};

/**
 * Wraps your component with a {@link BreadcrumbComponent}
 */
export var withBreadcrumb = function withBreadcrumb(Component, Breadcrumb, name) {
  var breadcrumbFields = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : emptyObject;
  var ComponentWithBreadcrumb = function ComponentWithBreadcrumb(props) {
    return /*#__PURE__*/React.createElement(Breadcrumb, _extends({}, breadcrumbFields, {
      name: name
    }), /*#__PURE__*/React.createElement(Component, props));
  };
  return ComponentWithBreadcrumb;
};